import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Send, X, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type Message = {
  room: string;
  user: string;
  text: string;
  time: string;
};

function socketUrl(): string | null {
  const enabled = (import.meta.env.VITE_CHAT_SOCKET_ENABLED as string | undefined)?.toLowerCase() === 'true';
  if (!enabled) return null;
  const custom = import.meta.env.VITE_CHAT_SOCKET_URL as string | undefined;
  if (custom && custom.trim()) return custom.trim();
  return null;
}

function storageKey(room: string) {
  return `ahime_chat_${room}`;
}

export default function Chat({ room, currentUser }: { room: string; currentUser: any }) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey(room));
      if (saved) setMessages(JSON.parse(saved));
    } catch {
      setMessages([]);
    }
  }, [room]);

  useEffect(() => {
    localStorage.setItem(storageKey(room), JSON.stringify(messages.slice(-100)));
  }, [messages, room]);

  useEffect(() => {
    const url = socketUrl();
    if (!url) {
      setConnected(false);
      socketRef.current = null;
      return;
    }

    const socket = io(url, {
      transports: ['polling'],
      upgrade: false,
      timeout: 2500,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join_room', room);
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('receive_message', (data: Message) => setMessages((prev) => [...prev, data]));

    return () => {
      socket.disconnect();
    };
  }, [room]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = () => {
    const text = message.trim();
    if (!text) return;

    const data: Message = {
      room,
      user: currentUser?.name || currentUser?.email || 'Utilisateur',
      text,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    if (connected && socketRef.current) {
      socketRef.current.emit('send_message', data);
    } else {
      setMessages((prev) => [...prev, data]);
    }
    setMessage('');
  };

  return (
    <div className="fixed bottom-8 right-8 z-[100]">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="bg-white w-80 h-[450px] rounded-[2.5rem] shadow-2xl border border-slate-100 flex flex-col overflow-hidden mb-4"
          >
            <div className="bg-slate-900 p-6 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-600 rounded-2xl flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-sm">Chat d'equipe</p>
                  <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">{connected ? 'En ligne' : 'Mode local'}</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50">
              {messages.map((msg, i) => (
                <div key={`${msg.time}-${i}`} className={`flex flex-col ${msg.user === (currentUser?.name || currentUser?.email) ? 'items-end' : 'items-start'}`}>
                  <div className={`max-w-[80%] p-4 rounded-2xl text-sm font-medium ${msg.user === (currentUser?.name || currentUser?.email) ? 'bg-red-600 text-white rounded-tr-none' : 'bg-white text-slate-900 border border-slate-100 rounded-tl-none shadow-sm'}`}>
                    <p className="text-[10px] opacity-50 mb-1 font-black uppercase tracking-widest">{msg.user}</p>
                    <p>{msg.text}</p>
                  </div>
                  <span className="text-[9px] text-slate-400 mt-1 font-bold">{msg.time}</span>
                </div>
              ))}
              <div ref={scrollRef} />
            </div>

            <div className="p-4 bg-white border-t border-slate-100">
              <div className="relative flex items-center gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Votre message..."
                  className="w-full pl-4 pr-12 py-3 rounded-xl bg-slate-50 border-transparent focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none transition-all text-sm font-medium"
                />
                <button onClick={sendMessage} className="absolute right-2 w-8 h-8 bg-red-600 text-white rounded-lg flex items-center justify-center hover:bg-red-700 transition-all active:scale-90 shadow-lg shadow-red-600/20">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button onClick={() => setIsOpen(!isOpen)} className="w-16 h-16 bg-slate-900 text-white rounded-3xl flex items-center justify-center shadow-2xl shadow-slate-900/40 hover:scale-110 transition-all active:scale-95 group relative">
        <MessageCircle className="w-7 h-7 group-hover:rotate-12 transition-transform" />
        <span className={`absolute -top-1 -right-1 w-5 h-5 border-4 border-white rounded-full ${connected ? 'bg-red-500' : 'bg-slate-400'}`}></span>
      </button>
    </div>
  );
}
